import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useWebAuthn } from '@/hooks/useWebAuthn';
import {
  ArrowLeft,
  Save,
  User,
  Loader2,
  Mail,
  Phone,
  Key,
  Fingerprint,
  Plus,
  Trash2,
  Smartphone,
  Shield,
  Bell,
} from 'lucide-react';

export default function Profile() {
  const { user, currentMembership, refreshMemberships } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [preferEmail, setPreferEmail] = useState(true);

  // WebAuthn/Passkey state
  const {
    isSupported: passkeysSupported,
    isLoading: passkeysLoading,
    error: passkeysError,
    credentials: passkeys,
    registerPasskey,
    loadCredentials: loadPasskeys,
    deleteCredential: deletePasskey,
    clearError: clearPasskeysError,
  } = useWebAuthn();

  // Email preferences state
  const [emailPrefs, setEmailPrefs] = useState({
    matchday_open: true,
    matchday_reminder: true,
    matchday_selections: true,
    matchday_results: true,
  });
  const [emailPrefsLoaded, setEmailPrefsLoaded] = useState(false);
  const [savingEmailPrefs, setSavingEmailPrefs] = useState(false);

  useEffect(() => {
    if (user) {
      loadProfile();
      loadPasskeys();
    }
    // Re-run when currentMembership loads to pick up the display_name
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, currentMembership?.display_name]);

  // Load email preferences when membership is available
  useEffect(() => {
    if (user?.id && currentMembership?.league_id) {
      loadEmailPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, currentMembership?.league_id]);

  const loadEmailPreferences = async () => {
    if (!user?.id || !currentMembership?.league_id) return;

    const { data, error } = await supabase
      .from('member_email_preferences' as string)
      .select('matchday_open, matchday_reminder, matchday_selections, matchday_results')
      .eq('user_id', user.id)
      .eq('league_id', currentMembership.league_id)
      .single();

    if (!error && data) {
      setEmailPrefs({
        matchday_open: data.matchday_open as boolean,
        matchday_reminder: data.matchday_reminder as boolean,
        matchday_selections: data.matchday_selections as boolean,
        matchday_results: data.matchday_results as boolean,
      });
    }
    // No row = all enabled (default), which is already the initial state
    setEmailPrefsLoaded(true);
  };

  const handleEmailPrefToggle = async (key: keyof typeof emailPrefs, value: boolean) => {
    if (!user?.id || !currentMembership?.league_id) return;

    const prevPrefs = { ...emailPrefs };
    const newPrefs = { ...emailPrefs, [key]: value };
    setEmailPrefs(newPrefs);
    setSavingEmailPrefs(true);

    const { error } = await supabase
      .from('member_email_preferences' as string)
      .upsert(
        {
          user_id: user.id,
          league_id: currentMembership.league_id,
          ...newPrefs,
        },
        { onConflict: 'user_id,league_id' }
      );

    if (error) {
      // Revert on error
      setEmailPrefs(prevPrefs);
      toast({
        title: 'Failed to update email preference',
        description: error.message,
        variant: 'destructive',
      });
    }
    setSavingEmailPrefs(false);
  };

  const loadProfile = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Get user's email from auth
      setEmail(user.email || '');

      // Load additional profile data if we have a profiles table
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      // Determine the best display name to use:
      // 1. Current membership display_name (league-specific name)
      // 2. Profile name from database
      // 3. Fall back to empty string
      const membershipName = currentMembership?.display_name;
      const profileName = !error && profile?.name ? profile.name : null;
      const bestDisplayName = membershipName || profileName || '';
      
      setDisplayName(bestDisplayName);

      if (!error && profile) {
        if (profile.mobile) {
          setMobile(profile.mobile);
        }
        if (profile.communication_preference) {
          setPreferEmail(profile.communication_preference === 'email');
        }
      }
    } catch (e) {
      console.error('Exception loading profile:', e);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!user) return;

    if (!displayName.trim()) {
      toast({
        title: 'Name Required',
        description: 'Please enter your display name.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);

    try {
      // Upsert profile in profiles table (insert if not exists, update if exists)
      // This handles new users in the multi-tenant system who may not have a profile row
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          email: user.email || '',
          name: displayName.trim(),
          mobile: mobile.trim() || null,
          communication_preference: preferEmail ? 'email' : 'sms',
        }, {
          onConflict: 'id',
        });

      if (profileError) {
        throw profileError;
      }

      // Update display_name in current league membership via RPC (bypasses strict RLS WITH CHECK)
      let membershipUpdateFailed = false;
      if (currentMembership) {
        const { error: membershipError } = await supabase
          .rpc('update_own_display_name', {
            p_membership_id: currentMembership.id,
            p_display_name: displayName.trim(),
          });

        if (membershipError) {
          console.warn('Failed to update membership display name:', membershipError);
          membershipUpdateFailed = true;
        }
      }

      // Update email if changed
      let emailUpdateFailed = false;
      const trimmedEmail = email.trim().toLowerCase();
      if (trimmedEmail && trimmedEmail !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({ email: trimmedEmail });
        if (emailError) {
          console.warn('Failed to update email:', emailError);
          emailUpdateFailed = true;
        }
      }

      // Refresh memberships so the updated display name is reflected everywhere
      if (!membershipUpdateFailed) {
        refreshMemberships();
      }

      if (membershipUpdateFailed || emailUpdateFailed) {
        const issues = [];
        if (membershipUpdateFailed) issues.push('display name in league standings');
        if (emailUpdateFailed) issues.push('email address');
        toast({
          title: 'Profile partially saved',
          description: `Your profile was saved, but ${issues.join(' and ')} could not be updated. Please try again.`,
          variant: 'destructive',
        });
      } else if (trimmedEmail && trimmedEmail !== user.email) {
        toast({
          title: 'Profile saved',
          description: 'A confirmation link has been sent to your new email address.',
        });
      } else {
        toast({ title: 'Profile saved' });
      }
    } catch (e) {
      console.error('Error saving profile:', e);
      toast({
        title: 'Failed to save',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    }

    setSaving(false);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center">
        <p className="text-stone-600">Please sign in</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100">
      {/* Header */}
      <div className="sticky top-0 z-50 pt-[env(safe-area-inset-top)] bg-gradient-to-r from-stone-800 to-stone-900">
        <header className="text-white shadow-lg">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold">Profile Settings</h1>
                <p className="text-sm text-stone-400">Manage your account</p>
              </div>
            </div>
          </div>
        </header>
      </div>

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Your Profile
            </CardTitle>
            <CardDescription>
              Update your personal information and preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your name"
                  maxLength={50}
                />
                <p className="text-xs text-stone-500">
                  This is how you'll appear to others in the league
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                />
                <p className="text-xs text-stone-500">
                  Changing your email requires confirmation at both old and new addresses
                </p>
              </div>

              {/* TODO: Implement mobile number and communication preference later
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number</Label>
                <Input
                  id="mobile"
                  type="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="Enter your mobile number"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {preferEmail ? (
                    <Mail className="h-5 w-5 text-blue-600" />
                  ) : (
                    <Phone className="h-5 w-5 text-green-600" />
                  )}
                  <div>
                    <Label htmlFor="communication-preference" className="font-medium">
                      Preferred Communication
                    </Label>
                    <p className="text-sm text-stone-600">
                      {preferEmail ? 'Email notifications' : 'Text notifications'}
                    </p>
                  </div>
                </div>
                <Switch
                  id="communication-preference"
                  checked={preferEmail}
                  onCheckedChange={setPreferEmail}
                />
              </div>
              */}
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Security Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Security
            </CardTitle>
            <CardDescription>Manage your account security</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Password</p>
                <p className="text-sm text-stone-500">
                  Change your password or set up a new one
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/forgot-password')}
              >
                Change Password
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Email Preferences Section */}
        {currentMembership && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Email Preferences
              </CardTitle>
              <CardDescription>
                Choose which emails you receive for {currentMembership.league?.name || 'this league'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Matchday Open</p>
                  <p className="text-xs text-stone-500">When picks open for a new matchday</p>
                </div>
                <Switch
                  checked={emailPrefs.matchday_open}
                  onCheckedChange={(checked) => handleEmailPrefToggle('matchday_open', checked)}
                  disabled={savingEmailPrefs || !emailPrefsLoaded}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Deadline Reminders</p>
                  <p className="text-xs text-stone-500">Reminders before pick deadline</p>
                </div>
                <Switch
                  checked={emailPrefs.matchday_reminder}
                  onCheckedChange={(checked) => handleEmailPrefToggle('matchday_reminder', checked)}
                  disabled={savingEmailPrefs || !emailPrefsLoaded}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Matchday Selections</p>
                  <p className="text-xs text-stone-500">Everyone's picks after the deadline</p>
                </div>
                <Switch
                  checked={emailPrefs.matchday_selections}
                  onCheckedChange={(checked) => handleEmailPrefToggle('matchday_selections', checked)}
                  disabled={savingEmailPrefs || !emailPrefsLoaded}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Matchday Results</p>
                  <p className="text-xs text-stone-500">Results and standings after matches</p>
                </div>
                <Switch
                  checked={emailPrefs.matchday_results}
                  onCheckedChange={(checked) => handleEmailPrefToggle('matchday_results', checked)}
                  disabled={savingEmailPrefs || !emailPrefsLoaded}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Passkeys Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Fingerprint className="h-5 w-5" />
              Passkeys
            </CardTitle>
            <CardDescription>
              Sign in quickly and securely with Touch ID, Face ID, or Windows Hello
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!passkeysSupported ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-800">
                  <Shield className="h-5 w-5" />
                  <p className="text-sm font-medium">
                    Passkeys are not supported on this device
                  </p>
                </div>
                <p className="text-sm text-amber-700 mt-1">
                  Try using a device with Touch ID, Face ID, or Windows Hello.
                </p>
              </div>
            ) : (
              <>
                {/* Error message */}
                {passkeysError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-red-800">{passkeysError}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearPasskeysError}
                        className="text-red-800 hover:text-red-900 hover:bg-red-100"
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                )}

                {/* Registered passkeys list */}
                {passkeys.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Your Passkeys</Label>
                    <div className="space-y-2">
                      {passkeys.map((passkey) => (
                        <div
                          key={passkey.id}
                          className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-stone-200 rounded-lg">
                              {passkey.device_type === 'multiDevice' ? (
                                <Shield className="h-4 w-4 text-stone-600" />
                              ) : (
                                <Smartphone className="h-4 w-4 text-stone-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{passkey.name}</p>
                              <p className="text-xs text-stone-500">
                                Added {new Date(passkey.created_at).toLocaleDateString()}
                                {passkey.last_used_at && (
                                  <> · Last used {new Date(passkey.last_used_at).toLocaleDateString()}</>
                                )}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              const success = await deletePasskey(passkey.id);
                              if (success) {
                                toast({ title: 'Passkey removed' });
                              } else {
                                toast({
                                  title: 'Failed to remove passkey',
                                  variant: 'destructive',
                                });
                              }
                            }}
                            disabled={passkeysLoading}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add passkey button */}
                <div className="flex items-center justify-between pt-2">
                  <div>
                    <p className="font-medium text-sm">Add a new passkey</p>
                    <p className="text-xs text-stone-500">
                      Use your device's biometric authentication
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      const success = await registerPasskey();
                      if (success) {
                        toast({ title: 'Passkey added successfully' });
                      }
                    }}
                    disabled={passkeysLoading}
                  >
                    {passkeysLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Add Passkey
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

